import {
  faBolt,
  faClock,
  faCube,
  faDatabase,
} from "@fortawesome/pro-regular-svg-icons";
import { faArrowDownRight } from "@fortawesome/pro-solid-svg-icons";
import cronstrue from "cronstrue";

import { Button } from "src/base-components/Button";
import { Pill } from "src/base-components/Pill";
import { FlowVersionStatus } from "src/clients/flow-api";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { useActivateJob } from "src/jobs/api/queries";
import { UNTITLED_DESTINATION } from "src/jobs/common/DestinationDropdown";
import { useUsedVersions } from "src/jobs/common/hooks";
import { getConfigurationString } from "src/jobs/JobContent";
import { getFutureMatches, isJobRunnable } from "src/jobs/jobUtils";
import { Job } from "src/jobs/types";
import { useFlowContext } from "src/router/routerContextHooks";
import { formatDateWithLocalTimezone } from "src/utils/datetime";
import { logger } from "src/utils/logger";
import { getLocalTimezone, getTimezoneAbbreviation } from "src/utils/timezones";

type ActivateJobModalProps = {
  isOpen: boolean;
  onClose: () => void;
  job?: Job;
};

export const ActivateJobModal: React.FC<ActivateJobModalProps> = ({
  isOpen,
  onClose,
  job,
}) => {
  const { workspace, flow } = useFlowContext();
  const usedVersions = useUsedVersions(job);
  const jobActivateMutation = useActivateJob(workspace.base_url!);

  const activateJob = async () => {
    try {
      if (!job) return;
      await jobActivateMutation.mutateAsync(job);
      onClose();
    } catch (error) {
      logger.error(error);
    }
  };

  const notAllVersionsArePublished = usedVersions.some(
    (v) => v.status !== FlowVersionStatus.PUBLISHED,
  );

  if (
    usedVersions.length === 0 ||
    notAllVersionsArePublished ||
    !isJobRunnable(job)
  ) {
    const getReasonMessage = () => {
      const isDraft = usedVersions.some(
        (v) => v.status === FlowVersionStatus.DRAFT,
      );

      if (isDraft) {
        return `An active Job cannot be run with a draft Decision Flow.`;
      }

      const isArchived = usedVersions.some(
        (v) => v.status === FlowVersionStatus.ARCHIVED,
      );
      if (isArchived) {
        return `An active Job cannot be run with an archived Decision Flow.`;
      }

      return `An active Job cannot be run with a Decision Flow.`;
    };
    return (
      <Modal open={isOpen} onClose={onClose}>
        <Modal.Header>Cannot activate Job</Modal.Header>
        <Modal.Content>
          {!isJobRunnable(job) ? (
            <div className="flex flex-col gap-y-2.5 font-inter-normal-13px text-gray-500">
              <p>The Job is missing the following details.</p>
              <ul className="ml-4 list-disc font-inter-medium-13px text-gray-700">
                {usedVersions.length === 0 && <li>Decision Flow</li>}
                {!job?.active_source && <li>Data Source</li>}
              </ul>
              <p>Please add the details to the Job before activating it.</p>
            </div>
          ) : (
            <div
              className="flex flex-col gap-y-2.5 font-inter-normal-13px text-gray-500"
              data-loc="activate-job-modal-invalid-version"
            >
              <p>{getReasonMessage()}</p>
              <p>
                This Job is configured to run{" "}
                <b className="font-inter-medium-13px text-gray-800">
                  {flow.name}
                </b>{" "}
                Flow with versions{" "}
                <b className="font-inter-medium-13px text-gray-800">
                  {new Intl.ListFormat("en", {
                    style: "long",
                    type: "conjunction",
                  }).format(usedVersions.map((v) => v.name))}
                </b>
                .
              </p>
              <p>
                Please publish all the Decision Flow versions before activating
                this job.
              </p>
            </div>
          )}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="activate-job"
              htmlType="submit"
              variant="primary"
              disabled
            >
              Activate Job
            </Button>
          }
        />
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header>Are you sure?</Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-y-2.5 font-inter-normal-13px text-gray-500">
          <p>
            Are you sure you want to make this Job active? This configuration
            will be used for the Job.
          </p>{" "}
          <div className="flex flex-col gap-y-2.5">
            <div className="flex items-center gap-x-0.5 text-gray-00 text-gray-800">
              <Icon icon={faDatabase} size="2xs" />{" "}
              <p className="max-w-40 truncate">{job?.active_source?.name}</p>
              <Pill size="sm" variant="gray">
                <Pill.Text>Live Connection</Pill.Text>
              </Pill>
            </div>
            {job?.active_destination && (
              <div className="flex items-center gap-x-0.5 text-gray-00 text-gray-800">
                <Icon icon={faArrowDownRight} size="2xs" />{" "}
                <p className="max-w-40 truncate">
                  {job.active_destination.name || UNTITLED_DESTINATION}
                </p>
                <Pill size="sm" variant="gray">
                  <Pill.Text>Live Connection</Pill.Text>
                </Pill>
              </div>
            )}
            <div className="flex items-center gap-x-1 text-gray-800">
              <Icon icon={faCube} size="2xs" /> {flow.name}
              <Pill size="sm" variant="gray">
                {usedVersions.length === 1 ? (
                  <Pill.Text>{usedVersions[0].name}</Pill.Text>
                ) : (
                  <Pill.Text>Multiple versions</Pill.Text>
                )}
              </Pill>
            </div>
            <div className="flex items-center gap-x-0.5 text-gray-800">
              <Icon icon={faClock} size="2xs" />
              {job?.schedule
                ? `${cronstrue.toString(job?.schedule, { verbose: true })} (${getTimezoneAbbreviation(job.schedule_timezone ?? getLocalTimezone())})`
                : "No schedule set"}
            </div>
            <div className="flex items-center gap-x-0.5 text-gray-800">
              <Icon icon={faBolt} size="2xs" />
              {getConfigurationString(job?.configuration)}
            </div>
          </div>
          <p>
            You cannot make any changes to the Job when it is in an active
            state. You can always deactivate it later.
          </p>
          {job?.schedule &&
            job.schedule_timezone &&
            (() => {
              const nextRun = getFutureMatches(
                job.schedule,
                job.schedule_timezone,
              );
              return (
                <p>
                  This Job is scheduled to run at{" "}
                  <span className="text-gray-800">
                    {formatDateWithLocalTimezone(
                      nextRun,
                      "iiii, d MMMM h:mmaaa",
                    )}
                  </span>
                </p>
              );
            })()}
          Please confirm if you wish to proceed.
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="activate-job"
            htmlType="submit"
            loading={jobActivateMutation.isPending}
            variant="primary"
            onClick={activateJob}
          >
            Activate Job
          </Button>
        }
      />
    </Modal>
  );
};
